import { TreeList } from '@ibsys/kit';
import { IconButton, TruncatedText } from '@/shared/ui';
import { useSortable } from '@dnd-kit/react/sortable';
import { OptimisticSortingPlugin } from '@dnd-kit/dom/sortable';
import { ArrowRightIcon, GripVerticalIcon, PackageIcon, PlusIcon } from 'lucide-react';
import { FC, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from '@/entities/profile';
import { cn } from '@/shared/lib';
import { BlockPointsRuleMarks } from './BlockPointsRuleMarks';
import { useIsNodeCollapsed } from './expandedNodesContext';
import { hasDescendant, type TreeNodeData, type TreeNodeType } from './treeUtils';

type ExpertReportTreeNodeProps = {
  node: TreeNodeData;
  /** Порядковый номер среди блочных дочерних элементов родителя */
  index: number;
  /** ID группы сортировки (id родительского узла) */
  groupId: string;
  level?: number;
  activeId: string;
  onSelect: (nodeId: string, type: TreeNodeType) => void;
  onAddBlock?: (reportId: number, parentBlockId?: number) => void;
  onExpandBlock: (id: string) => void;
  isDesktop?: boolean;
};

const ICON_SIZE = 18;

export const ExpertReportTreeNode: FC<ExpertReportTreeNodeProps> = ({
  node,
  index,
  groupId,
  level = 0,
  activeId,
  onSelect,
  onAddBlock,
  onExpandBlock,
  isDesktop,
}) => {
  const { t } = useTranslation('expertReport');
  const allowAdmin = useHasPermission('ADMIN_EXPERT_REPORT');
  const hasChildren = node.children && node.children.length > 0;
  const isCollapsed = useIsNodeCollapsed(node.id);

  // Пока блок свёрнут, отметки его вложенных блоков не видны — он показывает отметку и о них.
  // У раскрытого блока отметку несёт сам проблемный блок либо свёрнутый узел на пути к нему.
  const nestedAttention = isCollapsed ? node.nestedPointsRuleAttention : null;

  // Авто-раскрытие этого блока, если в нём содержится активный узел
  const isAutoExpandDone = useRef(false);
  useEffect(() => {
    if (isAutoExpandDone.current || !activeId || !hasChildren || !node.children) return;
    if (hasDescendant(node.children, activeId)) {
      onExpandBlock(node.id);
    }
    isAutoExpandDone.current = true;
  }, [activeId, hasChildren, node.children, node.id, onExpandBlock]);

  const isActive = activeId === node.id;
  const canAdd = node.type === 'block';

  // Делаем этот блок сортируемым в контексте родителя. Плагин оптимистичной сортировки отключаем:
  // во время перетаскивания он императивно переносит реальный DOM-узел между контейнерами уровней
  // (insertAdjacentElement), и последующая перерисовка React после броска расходится с уже
  // перенесённым узлом — отсюда падение при межуровневом переносе. Итоговую раскладку всё равно
  // вычисляет хелпер `move` из снимка групп и геометрии указателя, поэтому отключение плагина на
  // расчёт перестановки не влияет. Индикатор места вставки ведёт единый DropIndicator над деревом —
  // одна подписка на движение указателя вместо подписки в каждом узле.
  const { ref, handleRef, isDragging } = useSortable({
    id: node.id,
    index,
    group: groupId,
    disabled: node.type !== 'block' || !allowAdmin,
    plugins: (defaults) => defaults.filter((plugin) => plugin !== OptimisticSortingPlugin),
  });

  const handleSelect = () => {
    onSelect(node.id, node.type);
  };

  /* На десктопе клик по заголовку блока-группы открывает карточку.
     На мобильном клик только раскрывает аккордеон — навигация через rightSlot-кнопку. */
  const handleRowClick = () => {
    if (isDesktop) {
      onSelect(node.id, node.type);
    }
  };

  const handleAdd = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (node.type === 'block' && node.reportId && node.blockId) {
      onAddBlock?.(node.reportId, node.blockId);
    }
  };

  // Дочерние блоки для вложенного сортируемого контекста
  const blockChildren = useMemo(() => (node.children ?? []).filter((c) => c.type === 'block'), [node.children]);

  // Для листовых блоков (без дочерних блоков) — простая строка, без раскрытия
  if (node.type === 'block' && !hasChildren) {
    return (
      <div ref={ref} className={cn(isDragging && 'opacity-50')}>
        <TreeList.Item value={node.id} onClick={handleSelect} className="group/leaf">
          <div className="flex flex-1 items-start gap-2">
            {/* Иконка типа узла */}
            <PackageIcon
              size={ICON_SIZE}
              className={cn(
                'mt-0.5 shrink-0',
                isActive ? 'text-accent-foreground' : 'text-muted-foreground group-hover/leaf:text-foreground',
              )}
            />

            {/* Метка */}
            <TruncatedText lines={2} className="flex-1 text-left text-sm font-medium leading-tight">
              {node.label}
            </TruncatedText>

            <BlockPointsRuleMarks hasCustomPointsRule={node.hasCustomPointsRule} attention={node.pointsRuleAttention} />

            {allowAdmin && (
              <div className={'flex items-center gap-1 shrink-0'}>
                {/* Кнопка добавления подблока */}
                {canAdd && (
                  <IconButton
                    label={t('addSubBlock')}
                    icon={<PlusIcon className="size-3.5" />}
                    variant="ghost"
                    size="icon"
                    className="ml-auto size-6 shrink-0 opacity-0 transition-opacity group-hover/leaf:opacity-100"
                    onClick={handleAdd}
                  />
                )}
                {/* Ручка перетаскивания: бокс size-6 с центрированием — выравнивается с кнопкой «+» */}
                <div
                  className="flex size-6 shrink-0 cursor-grab items-center justify-center text-muted-foreground opacity-0 transition-opacity group-hover/leaf:opacity-100"
                  ref={(el) => handleRef(el)}
                  onClick={(e) => e.stopPropagation()}
                >
                  <GripVerticalIcon size={14} />
                </div>
              </div>
            )}
          </div>
        </TreeList.Item>
      </div>
    );
  }

  // Для блоков с дочерними блоками — раскрываемая группа
  if (node.type === 'block') {
    return (
      <div ref={ref} className={cn(isDragging && 'opacity-50')}>
        <TreeList.Group
          value={node.id}
          classNames={{ header: 'group/block' }}
          label={
            <div className="flex flex-1 items-start gap-2">
              {/* Иконка типа узла */}
              <PackageIcon
                size={ICON_SIZE}
                className={cn(
                  'mt-0.5 shrink-0',
                  isActive ? 'text-accent-foreground' : 'text-muted-foreground group-hover/block:text-foreground',
                )}
              />

              {/* Метка */}
              <TruncatedText lines={2} className="flex-1 text-left text-sm font-medium leading-tight">
                {node.label}
              </TruncatedText>

              <BlockPointsRuleMarks
                hasCustomPointsRule={node.hasCustomPointsRule}
                attention={node.pointsRuleAttention}
                nestedAttention={nestedAttention}
              />
            </div>
          }
          onClick={handleRowClick}
          rightSlot={
            <div className="flex shrink-0 items-center gap-0.5">
              {/* Кнопка перехода к карточке — только на мобильном */}
              {!isDesktop && (
                <IconButton
                  label={t('openCard')}
                  icon={<ArrowRightIcon className="size-3.5" />}
                  variant="ghost"
                  size="icon"
                  className="size-6 shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect(node.id, node.type);
                  }}
                />
              )}

              {/* Кнопки управления — только на десктопе, появляются по hover */}
              {isDesktop && allowAdmin && (
                <div className="flex gap-0.5 opacity-0 transition-opacity group-hover/block:opacity-100">
                  {canAdd && (
                    <IconButton
                      label={t('addSubBlock')}
                      icon={<PlusIcon className="size-3.5" />}
                      variant="ghost"
                      size="icon"
                      className="size-6 shrink-0"
                      onClick={handleAdd}
                    />
                  )}

                  {/* Ручка перетаскивания */}
                  <div
                    className="flex size-6 shrink-0 cursor-grab items-center justify-center text-muted-foreground"
                    ref={(el) => handleRef(el)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <GripVerticalIcon size={14} />
                  </div>
                </div>
              )}

              {/* Ручка перетаскивания — на мобильном всегда видна */}
              {!isDesktop && allowAdmin && (
                <div
                  className="flex size-6 shrink-0 cursor-grab items-center justify-center text-muted-foreground"
                  ref={(el) => handleRef(el)}
                  onClick={(e) => e.stopPropagation()}
                >
                  <GripVerticalIcon size={14} />
                </div>
              )}
            </div>
          }
        >
          <div className="relative">
            {/* Дочерние блоки, каждый с group=node.id для сортировки в контексте этого узла */}
            {node.children!.map((child) => {
              const blockIndex = blockChildren.indexOf(child);
              return (
                <ExpertReportTreeNode
                  key={child.id}
                  node={child}
                  index={blockIndex >= 0 ? blockIndex : 0}
                  groupId={node.id}
                  level={level + 1}
                  activeId={activeId}
                  onSelect={onSelect}
                  onAddBlock={onAddBlock}
                  onExpandBlock={onExpandBlock}
                  isDesktop={isDesktop}
                />
              );
            })}
          </div>
        </TreeList.Group>
      </div>
    );
  }

  return null;
};
